import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { updateRecipeBox } from "@/lib/recipe-box-service";
import type { RecipeBox } from "@/lib/types";
import { Plus, X } from "lucide-react";
import { useEffect, useState } from "react";

interface LabelsDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly recipeBox: RecipeBox | null;
  readonly userId: string | null;
  readonly onUpdate: () => void;
}

export function CustomCategoriesDialog({
  open,
  onOpenChange,
  recipeBox,
  userId,
  onUpdate,
}: LabelsDialogProps) {
  const [labels, setLabels] = useState<string[]>(recipeBox?.labels ?? []);
  const [newLabel, setNewLabel] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && recipeBox) {
      setLabels(recipeBox.labels ?? []);
      setNewLabel("");
    }
  }, [open, recipeBox]);

  const handleAddLabel = () => {
    const trimmed = newLabel.trim();
    if (trimmed && !labels.includes(trimmed)) {
      setLabels([...labels, trimmed]);
      setNewLabel("");
    }
  };

  const handleRemoveLabel = (labelToRemove: string) => {
    setLabels(labels.filter((l) => l !== labelToRemove));
  };

  const handleSave = async () => {
    if (!recipeBox || !userId) return;

    setIsSaving(true);
    try {
      await updateRecipeBox(userId, recipeBox.id, { labels });
      onUpdate();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update labels", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Manage Labels</DialogTitle>
          <DialogDescription>
            Add custom labels for this recipe box. These appear alongside predefined labels when editing recipes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-label">Add Label</Label>
            <div className="flex gap-2">
              <Input
                id="new-label"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddLabel();
                  }
                }}
                placeholder="Enter label name"
              />
              <Button type="button" onClick={handleAddLabel} disabled={!newLabel.trim()}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {labels.length > 0 ? (
            <div className="space-y-2">
              <Label>Custom Labels</Label>
              <div className="flex flex-wrap gap-2">
                {labels.map((label) => (
                  <div
                    key={label}
                    className="flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm"
                  >
                    <span>{label}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 hover:bg-destructive/20"
                      onClick={() => handleRemoveLabel(label)}
                      aria-label={`Remove ${label}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No custom labels yet. Add one above.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
